import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T, scoreColor } from "../../tokens";
import { useAuthStore } from "../../store";

type MatchRow = {
  matchId: string;
  userId: string; userName: string; userEmail: string;
  jobId: string; jobTitle: string; company: string; location: string | null;
  weightedScore: number | null; semanticScore: number | null;
  modelUsed: string | null; autoApplyEligible: boolean;
  isStale: boolean; staleReason: string | null;
  scoredAt: string; explanation: string;
};

type MatchesPage = { items: MatchRow[]; total: number; page: number; limit: number };

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

const MODEL_LABEL: Record<string, string> = {
  "needs_jd": "needs_jd",
  "semantic_only": "semantic",
  "fallback_semantic_only": "fallback",
  "llm_skipped_budget": "budget",
  "llm_failed_rate_limit": "rate_limit",
};

function modelLabel(m: string | null) {
  if (!m) return "—";
  if (m.startsWith("llm_scored:")) return `✓ ${m.replace("llm_scored:", "")}`;
  return MODEL_LABEL[m] ?? m;
}

export function AdminCrossMatches() {
  const token = useAuthStore((s) => s.token);
  const [page, setPage] = useState(1);
  const [filterStale, setFilterStale] = useState<"" | "true" | "false">("");
  const [filterAuto, setFilterAuto] = useState(false);

  const q = useQuery<MatchesPage>({
    queryKey: ["admin-matches", page, filterStale, filterAuto],
    queryFn: async () => {
      const params: Record<string, string> = { page: String(page), limit: "50" };
      if (filterStale)  params.isStale   = filterStale;
      if (filterAuto)   params.autoApply = "true";
      const { data } = await axios.get("/api/admin/matches", { params });
      return data;
    },
    enabled: !!token, retry: false,
  });

  const totalPages = q.data ? Math.ceil(q.data.total / q.data.limit) : 0;

  const filterBtn = (active: boolean) => ({
    padding: "5px 10px", borderRadius: "5px", cursor: "pointer",
    fontFamily: T.mono, fontSize: "10px", letterSpacing: "0.06em",
    background: active ? T.tealBg : T.s2,
    border: `1px solid ${active ? T.tealBdr : T.b1}`,
    color: active ? T.tealSoft : T.t2,
  } as React.CSSProperties);

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px", alignItems: "center" }}>
        <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3 }}>Filter:</span>
        <button type="button"
          style={filterBtn(filterStale === "")}
          onClick={() => { setFilterStale(""); setPage(1); }}>All</button>
        <button type="button"
          style={filterBtn(filterStale === "false")}
          onClick={() => { setFilterStale("false"); setPage(1); }}>Active only</button>
        <button type="button"
          style={filterBtn(filterStale === "true")}
          onClick={() => { setFilterStale("true"); setPage(1); }}>Stale only</button>
        <button type="button"
          style={filterBtn(filterAuto)}
          onClick={() => { setFilterAuto((v) => !v); setPage(1); }}>Auto-apply eligible</button>
      </div>

      {q.isLoading && <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading…</p>}
      {q.isError   && <p style={{ fontFamily: T.body, fontSize: "13px", color: T.err }}>Failed to load matches.</p>}

      {q.data && (
        <>
          <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
            {q.data.total.toLocaleString()} match{q.data.total !== 1 ? "es" : ""}
          </div>

          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "grid",
              gridTemplateColumns: "1fr 1fr 90px 44px 44px 60px",
              padding: "8px 16px", borderBottom: `1px solid ${T.b0}`,
              fontFamily: T.mono, fontSize: "9px", color: T.t3,
              letterSpacing: "0.10em", textTransform: "uppercase" }}>
              <span>User</span>
              <span>Job / Company</span>
              <span>Model</span>
              <span style={{ textAlign: "center" }}>Stale</span>
              <span style={{ textAlign: "center" }}>Auto</span>
              <span style={{ textAlign: "right" }}>Score</span>
            </div>

            {q.data.items.length === 0 && (
              <div style={{ padding: "24px 16px", fontFamily: T.body, fontSize: "13px",
                color: T.t3, textAlign: "center" }}>No matches found.</div>
            )}

            {q.data.items.map((m, i) => {
              const score = Math.round(m.weightedScore ?? 0);
              return (
                <div key={m.matchId} style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 90px 44px 44px 60px",
                  padding: "8px 16px",
                  borderBottom: i < q.data!.items.length - 1 ? `1px solid ${T.b0}` : "none",
                  background: m.isStale ? T.s2 : "transparent",
                }}>
                  {/* User */}
                  <div style={{ overflow: "hidden" }}>
                    <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t0,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.userName}</div>
                    <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.userEmail}</div>
                  </div>

                  {/* Job */}
                  <div style={{ overflow: "hidden" }}>
                    <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t0,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.jobTitle}</div>
                    <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.company}</div>
                    <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3 }}>{fmtDate(m.scoredAt)}</div>
                  </div>

                  {/* Model */}
                  <span style={{ fontFamily: T.mono, fontSize: "9px",
                    color: m.modelUsed?.startsWith("llm_scored") ? T.ok : T.t3,
                    alignSelf: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {modelLabel(m.modelUsed)}
                  </span>

                  {/* Stale */}
                  <span style={{ fontFamily: T.mono, fontSize: "11px", textAlign: "center",
                    color: m.isStale ? T.err : T.t3, alignSelf: "center" }}>
                    {m.isStale ? "✗" : "·"}
                  </span>

                  {/* Auto */}
                  <span style={{ fontFamily: T.mono, fontSize: "11px", textAlign: "center",
                    color: m.autoApplyEligible ? T.ok : T.t3, alignSelf: "center" }}>
                    {m.autoApplyEligible ? "✓" : "·"}
                  </span>

                  {/* Score */}
                  <span style={{ fontFamily: T.mono, fontSize: "13px", fontWeight: 600,
                    textAlign: "right", color: score > 0 ? scoreColor(score) : T.t3,
                    fontVariantNumeric: "tabular-nums", alignSelf: "center" }}>
                    {score > 0 ? score : "—"}
                  </span>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center",
              marginTop: "12px", justifyContent: "flex-end" }}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                style={{ padding: "5px 10px", background: T.s2, border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: page <= 1 ? T.t3 : T.t0,
                  fontFamily: T.body, fontSize: "12px", cursor: page <= 1 ? "default" : "pointer" }}>‹ Prev</button>
              <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2 }}>{page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                style={{ padding: "5px 10px", background: T.s2, border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: page >= totalPages ? T.t3 : T.t0,
                  fontFamily: T.body, fontSize: "12px", cursor: page >= totalPages ? "default" : "pointer" }}>Next ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
